import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Phone,
  RefreshCw,
  ChevronDown,
  Play,
  ExternalLink,
} from "lucide-react";
import { TrendUp } from "iconsax-reactjs";
import apiService from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";

// ─── Utility ────────────────────────────────────────────────────────────────
const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

const formatPhone = (phone) => {
  if (!phone) return "—";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1"))
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
};

const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Sub-components ─────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, change, isDarkMode }) => (
  <div
    className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50" : "bg-white border-gray-200 hover:border-gray-300"} rounded-xl p-6 border transition-colors`}
  >
    <div
      className={`${isDarkMode ? "text-[#94979C]" : "text-gray-500"} text-[14px] font-medium mb-3`}
    >
      {title}
    </div>
    <div className="flex items-end justify-between">
      <div
        className={`${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"} text-[28px] xl:text-[20px] font-bold`}
      >
        {value}
      </div>
      {change !== undefined && (
        <div
          className={`flex items-center ${isDarkMode ? "text-white" : "text-gray-700"} text-sm xl:text-[10px] font-medium`}
        >
          <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
          {change}%
        </div>
      )}
    </div>
    {sub && (
      <div
        className={`${isDarkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}
      >
        {sub}
      </div>
    )}
  </div>
);

const QualBadge = ({ record, isDarkMode }) => {
  if (record.isQualified === true) {
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium text-green-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`}
      >
        <div className="w-2 h-2 rounded-full bg-green-400 mr-2" />
        Qualified
        {record.qualificationScore != null && (
          <span className="ml-1.5 text-green-300 opacity-70">
            ({record.qualificationScore})
          </span>
        )}
      </span>
    );
  }
  if (record.isQualified === false) {
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium text-red-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`}
      >
        <div className="w-2 h-2 rounded-full bg-red-400 mr-2" />
        Unqualified
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`}
    >
      <div className="w-2 h-2 rounded-full bg-gray-500 mr-2" />
      Pending
    </span>
  );
};

const CallRow = ({ record, navigate, isDarkMode }) => {
  const leadName = record.lead?.fullName || "Unknown Lead";
  const leadInitials = leadName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <tr
      className={`border-b ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"} transition-colors`}
    >
      {/* Lead */}
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-300 text-gray-600"} flex items-center justify-center text-sm font-medium shrink-0`}
          >
            {leadInitials}
          </div>
          <div>
            <button
              onClick={() => navigate(`/leads/${record.leadId}`)}
              className={`font-medium hover:text-blue-400 transition-colors text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {leadName}
            </button>
            <div
              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              {record.lead?.email || "—"}
            </div>
          </div>
        </div>
      </td>

      {/* Phone */}
      <td className="py-4 px-4">
        <a
          href={`tel:${record.phoneNumber}`}
          className="text-blue-400 hover:text-blue-300 transition-colors hover:underline text-sm"
        >
          {formatPhone(record.phoneNumber)}
        </a>
      </td>

      {/* Duration */}
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <Play
            size={13}
            className={`${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
          />
          <span
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            {formatDuration(record.duration)}
          </span>
        </div>
      </td>

      {/* Started */}
      <td className="py-4 px-4">
        <span
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {formatDate(record.callStartTime)}
        </span>
      </td>

      {/* Qualification */}
      <td className="py-4 px-4">
        <QualBadge record={record} isDarkMode={isDarkMode} />
      </td>

      {/* Summary snippet */}
      <td className="py-4 px-4 max-w-[200px]">
        {record.summary ? (
          <span
            className={`text-sm truncate block ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {record.summary}
          </span>
        ) : (
          <span
            className={`text-sm ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
          >
            —
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="py-4 px-4 text-right">
        <button
          onClick={() => navigate(`/lead-call-data/${record.id}`)}
          className={`${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors p-1`}
          title="View details"
        >
          <ExternalLink size={15} />
        </button>
      </td>
    </tr>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────────
const LeadCallData = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 0,
  });

  const [search, setSearch] = useState("");
  const [qualFilter, setQualFilter] = useState(""); // "qualified" | "unqualified" | "pending" | ""
  const [sortBy, setSortBy] = useState("callStartTime");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [stats, setStats] = useState({
    totalCalls: 0,
    qualifiedCount: 0,
    avgDuration: 0,
    todayCount: 0,
  });

  // ─── Fetch ──────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
      };
      if (search) params.search = search;
      if (qualFilter) params.qualified = qualFilter;

      const response = await apiService.client.get("/lead-call-data", {
        params,
      });

      setRecords(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total ?? 0,
        totalPages: response.pagination?.totalPages ?? 0,
      }));

      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Error fetching lead call data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    search,
    qualFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ─── Sort toggle ────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "DESC" ? "ASC" : "DESC"));
    } else {
      setSortBy(field);
      setSortOrder("DESC");
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field)
      return <ChevronDown size={13} className="opacity-30" />;
    return (
      <ChevronDown
        size={13}
        className={`transition-transform ${sortOrder === "ASC" ? "rotate-180" : ""}`}
      />
    );
  };

  // ─── Qual filter options ────────────────────────────────────────────────
  const qualOptions = [
    { value: "", label: "All" },
    { value: "qualified", label: "Qualified" },
    { value: "unqualified", label: "Unqualified" },
    { value: "pending", label: "Pending" },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Call Data
            </h1>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-400" : "text-gray-500"} leading-relaxed`}
            >
              Review all recorded calls linked to your leads.
            </p>
          </div>

          <div className="flex gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchRecords}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"} border rounded-lg transition-colors text-sm sm:text-base font-medium`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Calls"
            value={stats.totalCalls}
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Qualified"
            value={stats.qualifiedCount}
            sub={`of ${stats.totalCalls} calls`}
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats.avgDuration)}
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Calls Today"
            value={stats.todayCount}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {/* Search */}
            <div className="relative">
              <Search
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              />
              <input
                type="text"
                placeholder="Search by phone or lead name…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={`pl-9 pr-4 py-2 ${isDarkMode ? "bg-[#141820] border-gray-700 text-gray-300 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-lg focus:outline-none focus:border-blue-500 text-sm w-64 transition-colors`}
              />
            </div>

            {/* Qualification filter */}
            <div className="flex gap-1.5">
              {qualOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setQualFilter(opt.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                    qualFilter === opt.value
                      ? "bg-blue-600/20 border-blue-600/40 text-blue-400"
                      : isDarkMode
                        ? "bg-[#141820] border-gray-700 text-gray-400 hover:border-gray-600"
                        : "bg-white border-gray-300 text-gray-500 hover:border-gray-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} rounded-lg border mb-8`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Call Records{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} records
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                All calls synced from your call system.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm`}
                  >
                    Lead
                  </th>
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm cursor-pointer hover:text-gray-200 transition-colors`}
                    onClick={() => handleSort("phoneNumber")}
                  >
                    <div className="flex items-center gap-1">
                      Phone <SortIcon field="phoneNumber" />
                    </div>
                  </th>
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm cursor-pointer hover:text-gray-200 transition-colors`}
                    onClick={() => handleSort("duration")}
                  >
                    <div className="flex items-center gap-1">
                      Duration <SortIcon field="duration" />
                    </div>
                  </th>
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm cursor-pointer hover:text-gray-200 transition-colors`}
                    onClick={() => handleSort("callStartTime")}
                  >
                    <div className="flex items-center gap-1">
                      Started <SortIcon field="callStartTime" />
                    </div>
                  </th>
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm`}
                  >
                    Qualification
                  </th>
                  <th
                    className={`text-left py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm`}
                  >
                    Summary
                  </th>
                  <th
                    className={`text-right py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} font-medium text-sm`}
                  >
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-12 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading call data…
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-12 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No call records found. They will appear here once calls
                      are synced from your call system.
                    </td>
                  </tr>
                ) : (
                  records.map((record) => (
                    <CallRow
                      key={record.id}
                      record={record}
                      navigate={navigate}
                      isDarkMode={isDarkMode}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} gap-4`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"} border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"} border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages || 1}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCallData;
